import React from "react";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { Checkbox, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { COMPONENT_MODES } from "../../../data/constants";
import { isAddNewMode, isViewMode } from "../../../helpers/catalog-helper";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import {
  allowEverything,
  decimalPriceRegex,
  removeSpecialCharacters,
  removeSpecialCharAndNumbers,
} from "../../../utils";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import DescriptionText from "../../../components/reusable-ui/description-text";
import { CMS_DESCRIPTION_TEXTS } from "../../../data/cms-description-text";
import { currency } from "../../../helpers/common-helper";

interface Props {
  data?: any;
  modeConfig?: any;
  setModeConfig?: any;
  updateChargeDataInParentState?: any;
  errorMessageMap?: any;
}
const currencySymbol = currency();
const ChargesDetailsEditView = (props: Props) => {
  const {
    data,
    modeConfig,
    setModeConfig,
    updateChargeDataInParentState,
    errorMessageMap,
  } = props;
  const taxTypes = useSelector(
    (state: RootState) => state.catalogReducer?.allCatalogDataMap?.taxTypes
  );
  const allApplicableModes = {
    pickup: {
      name: "Pickup",
      value: "Pickup",
    },
    delivery: {
      name: "Delivery",
      value: "Delivery",
    },
    others: {
      name: "Others",
      value: "Others",
    },
  };

  /**
   * This function will render the customTextField input with the help of textFieldConfig
   * @param textFieldConfig
   */
  function renderTextField(textFieldConfig: any): React.JSX.Element {
    return (
      <CustomTextField
        textBoxLabel={textFieldConfig.fieldLabel}
        placeholder={textFieldConfig.placeHolder}
        regexCheck={textFieldConfig.regexCheck || removeSpecialCharAndNumbers}
        handleChange={textFieldConfig.onSubmit}
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal
          ${textFieldConfig.customClass || ""}`}
        parentClass={`${textFieldConfig.boxWidth || ""} ${
          textFieldConfig.parentClass || ""
        }`}
        value={textFieldConfig.value}
        handleFocusOut={textFieldConfig.onBlur}
        rows={textFieldConfig.rows}
        isMandatory={!!textFieldConfig.isMandatory}
        errorMessage={errorMessageMap[textFieldConfig.key] || ""}
        iconClass={textFieldConfig.iconClass || ""}
        isDisabled={isViewMode(modeConfig?.type)}
        maxLength={textFieldConfig.maxLength}
      ></CustomTextField>
    );
  }

  function handleInputChange(value: any, fieldToUpdate: string) {
    updateChargeDataInParentState(fieldToUpdate, value);
  }

  function renderCustomDropDown(dropdownConfig: any): React.JSX.Element {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.textBoxLabel}
        optionsMap={dropdownConfig.optionsList}
        isMultiSelect={dropdownConfig.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={(data: any) => {
          if (dropdownConfig.getListItemText)
            return dropdownConfig.getListItemText(data);
          else return data?.name || "";
        }}
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={dropdownConfig.onSubmit}
        preSelectedValues={dropdownConfig.preSelectedValues}
        uniqueKey={dropdownConfig.uniqueKey || "mid"}
        isShowLoadMore={dropdownConfig.isShowLoadMore}
        onLoadMoreClick={dropdownConfig.onLoadMoreClick}
        onSearchApi={dropdownConfig.onSearchInput}
        isMandatory={!!dropdownConfig.isMandatory}
        errorMessage={errorMessageMap[dropdownConfig.key] || ""}
        isDisabled={isViewMode(modeConfig?.type)}
        enableSelectAll={!!dropdownConfig.enableSelectAll}
      ></CustomDropdown>
    );
  }

  return (
    <section className="w-[100%] bg-white p-[24px]">
      {!isAddNewMode(modeConfig.type) && (
        <>
          <div className="flex items-center justify-between mb-10">
            <p className="text-[20px] font-medium">Details</p>
            {isViewMode(modeConfig?.type) && (
              <div
                className="flex cursor-pointer"
                onClick={() =>
                  setModeConfig({
                    ...modeConfig,
                    type: COMPONENT_MODES.editMode,
                  })
                }
              >
                <div className="rista-sprite ic-pencil-blue"></div>
                <p className="text-14 text-blue font-semibold ml-4">Edit</p>
              </div>
            )}
          </div>
          <DescriptionText
            descriptionText={CMS_DESCRIPTION_TEXTS.chargeView.details}
            customClass="text-[13px] text-black/40 mb-[40px]"
          />
        </>
      )}

      {renderTextField({
        placeHolder: "Enter Charge Name, e.g, Service charge",
        fieldLabel: "Name",
        value: get(data, "name") || "",
        boxWidth: "w-full",
        isMandatory: true,
        key: "name",
        regexCheck: allowEverything,
        parentKey: "",
        onSubmit: (value: any) => handleInputChange(value, "name"),
        parentClass: "mt-[24px] mb-[6px]",
        maxLength: 30,
      })}

      {renderTextField({
        placeHolder: "Enter Display Label",
        fieldLabel: "Display Label",
        value: get(data, "label") || "",
        boxWidth: "w-full",
        isMandatory: false,
        key: "label",
        regexCheck: allowEverything,
        parentKey: "",
        onSubmit: (value: any) => handleInputChange(value, "label"),
        parentClass: "mb-[6px]",
        maxLength: 30,
      })}

      {renderTextField({
        placeHolder: "Enter Description",
        fieldLabel: "Description",
        value: get(data, "description") || "",
        boxWidth: "w-full",
        isMandatory: false,
        key: "description",
        regexCheck: allowEverything,
        parentKey: "",
        onSubmit: (value: any) => handleInputChange(value, "description"),
        parentClass: "mb-[6px]",
        maxLength: 150,
      })}

      <div className="mb-[6px]">
        <p className="text-[14px] text-[#656565] font-normal">
          Charge Amount <span className="text-[#FF0000]">*</span>
        </p>
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          value={get(data, "chargeType") || "Percentage"}
          onChange={(e) => handleInputChange(e.target.value, "chargeType")}
          name="row-radio-buttons-group"
        >
          <FormControlLabel
            value="Absolute"
            disabled={isViewMode(modeConfig?.type)}
            control={<Radio sx={{ fontSize: 12 }} />}
            label={`Amount (${currencySymbol})`}
          />
          <FormControlLabel
            value="Percentage"
            disabled={isViewMode(modeConfig?.type)}
            control={<Radio sx={{ fontSize: 12 }} />}
            label="Percentage (%)"
          />
        </RadioGroup>

        {renderTextField({
          placeHolder: "",
          fieldLabel: "",
          value: get(data, "chargeRate") || "",
          boxWidth: "",
          isMandatory: true,
          key: "chargeRate",
          regexCheck: decimalPriceRegex,
          parentKey: "",
          onSubmit: (value: any) => handleInputChange(value, "chargeRate"),
          onBlur: (e: any) => {
            const sanitized = decimalPriceRegex(e.target.value);   
            const numericValue = sanitized ? Number(sanitized) : null; 
            handleInputChange(numericValue, "chargeRate");
          },


          parentClass: "mt-[10px]",
          iconClass:
            get(data, "chargeType") === "Absolute"
              ? "ic-currency"
              : "ic-percentage-symbol",
        })}
      </div>

      {renderTextField({
        placeHolder: "",
        fieldLabel: "Tax Code",
        value: get(data, "chargeTaxCode") || "",
        boxWidth: "",
        isMandatory: false,
        key: "chargeTaxCode",
        regexCheck: removeSpecialCharacters,
        parentKey: "",
        onSubmit: (value: any) => handleInputChange(value, "chargeTaxCode"),
        parentClass: "mb-[6px]",
      })}

      <div className="mb-[12px]">
        <p className="text-[14px] text-[#656565] font-normal">
          Apply At <span className="text-[#FF0000]">*</span>
        </p>
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          value={get(data, "applyAt") || "Item"}
          onChange={(e) => handleInputChange(e.target.value, "applyAt")}
          name="row-radio-buttons-group"
        >
          <FormControlLabel
            value="Order"
            disabled={isViewMode(modeConfig?.type)}
            control={<Radio sx={{ fontSize: 12 }} />}
            label="Order"
          />
          <FormControlLabel
            value="Item"
            disabled={isViewMode(modeConfig?.type)}
            control={<Radio sx={{ fontSize: 12 }} />}
            label="Item"
          />
        </RadioGroup>
      </div>

      {get(data, "applyAt") === "Order" &&
        renderTextField({
          placeHolder: "",
          fieldLabel: "Minimum Order Amount for Waiver",
          value: get(data, "minOrderAmount") || "",
          boxWidth: "",
          isMandatory: false,
          key: "minOrderAmount",
          regexCheck: decimalPriceRegex,
          parentKey: "",
          onSubmit: (value: any) => handleInputChange(value, "minOrderAmount"),
          onBlur: (e: any) => {
            const sanitized = decimalPriceRegex(e.target.value);  
            const numericValue = sanitized ? Number(sanitized) : null; 
            handleInputChange(numericValue, "minOrderAmount");
          },
          parentClass: "mb-[6px]",
          iconClass: "ic-currency",
        })}

      {renderCustomDropDown({
        textBoxLabel: "Applicable Order Modes",
        optionsList: allApplicableModes || {},
        placeholder: "Select Order Modes",
        getListItemText: (data: any) => data?.name || "",
        dropdownHeading: "Order Modes",
        searchPlaceholder: "Search Order Mode",
        parentClass: "w-full mb-[6px]",
        isMultiSelect: true,
        enableSelectAll: true,
        preSelectedValues: data?.applicableModes || [],
        onSubmit: (value: any) => handleInputChange(value, "applicableModes"),
        key: "applicableModes",
        uniqueKey: "value",
      })}

      {renderCustomDropDown({
        textBoxLabel: "Taxes",
        optionsList: taxTypes,
        placeholder: "Select Taxes",
        getListItemText: (data: any) => data?.name || "",
        dropdownHeading: "Tax Types",
        searchPlaceholder: "Search Order Mode",
        parentClass: "w-full mb-[6px]",
        isMultiSelect: true,
        enableSelectAll: true,
        preSelectedValues: data?.taxTypeIds || [],
        onSubmit: (value: any) => handleInputChange(value, "taxTypeIds"),
        key: "taxTypeIds",
        uniqueKey: "mid",
      })}

      <div className="flex items-start justify-between">
        <div className="w-[calc(50%-12px)] mr-[24px] flex items-start">
          <Checkbox
            checked={!!get(data, "isIncludesTax")}
            onChange={(e) =>
              handleInputChange(e.target.checked, "isIncludesTax")
            }
            disabled={isViewMode(modeConfig?.type)}
            className="!p-0 !m-0"
          />
          <span className="ml-[6px] min-w-fit">Includes Taxes</span>
        </div>

        <div className="w-[calc(50%-12px)] flex items-start">
          <Checkbox
            checked={!!get(data, "isDirectCharge")}
            onChange={(e) =>
              handleInputChange(e.target.checked, "isDirectCharge")
            }
            disabled={isViewMode(modeConfig?.type)}
            className="!p-0 !m-0"
          />
          <span className="ml-[6px] min-w-fit">Direct Charges Applicable</span>
        </div>
      </div>
    </section>
  );
};

export default ChargesDetailsEditView;
